/**
 * Shared input for the analysis log and the printed report, and the rule for
 * which sections are current enough to include.
 *
 * A stale solution makes the whole report unavailable. Outdated detection
 * results or transformed output only omit their own sections, with a notice.
 */

import type {
  PointPair,
  TransformationSolution,
  BlunderDetectionResults,
  DataSnoopingResults,
  TransformedPoint,
} from '@/types';

export interface ReportInput {
  solutionStale?: boolean;
  combinatorialStale?: boolean;
  dataSnoopingStale?: boolean;
  transformedStale?: boolean;
  inputRevision?: number;
  solutionRevision?: number;
  solutionInputRevision?: number | null;
  appliedScale?: number | null;
  units?: string;
  transformedFromRevision?: number | null;
  solution: TransformationSolution | null;
  solutionTimestamp: number | null;
  pointPairs: PointPair[];
  blunderResults: BlunderDetectionResults | null;
  dataSnoopingResults: DataSnoopingResults | null;
  transformedPoints: TransformedPoint[];
}

export type PreparedReport =
  /** The solution no longer describes the inputs; no report may be produced. */
  | { status: 'unavailable' }
  /** `input` has outdated sections emptied; `omittedSections` names them. */
  | { status: 'ready'; input: ReportInput; omittedSections: string[] };

export const prepareReportInput = (input: ReportInput): PreparedReport => {
  if (input.solutionStale) return { status: 'unavailable' };
  const { combinatorialStale, dataSnoopingStale, transformedStale } = input;
  const omittedSections = [
    combinatorialStale ? 'combinatorial results' : '',
    dataSnoopingStale ? 'data snooping results' : '',
    transformedStale ? 'transformed coordinates' : '',
  ].filter(Boolean);
  return {
    status: 'ready',
    omittedSections,
    input: {
      ...input,
      blunderResults: combinatorialStale ? null : input.blunderResults,
      dataSnoopingResults: dataSnoopingStale ? null : input.dataSnoopingResults,
      transformedPoints: transformedStale ? [] : input.transformedPoints,
    },
  };
};
